import {
  Button,
  ButtonGroup,
  Modal,
  ModalBody,
  ModalFooter,
  notify,
  NumberField,
  Progress,
  Select,
  Stack,
  Tooltip,
} from '@eleveo/components-react';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CalendarCurrentDayIcon,
  EnvelopeFullIcon,
  QuestionSquareIcon,
} from '@eleveo/icons-react';
// eslint-disable-next-line import/no-extraneous-dependencies
import {
  isSameMonth,
  parseAbsoluteToLocal,
  parseDate,
  startOfMonth,
  startOfYear,
  toCalendarDate,
  toCalendarDateTime,
  today,
} from '@internationalized/date';
import React, { useEffect, useId, useMemo, useState } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useDateFormatter } from 'react-aria';
import { Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router';

import { Permissions, useAuth } from '../../../app/AuthorizationProvider';
import { Header } from '../../../components/LayoutNext/Header';
import { Content, Panel, SidebarWrapper } from '../../../components/LayoutNext/LayoutNext';
import { CreatePTORequestModalBody } from '../../../components/MySchedule/CreatePTORequestModalBody';
import { CreateShiftSwapRequestModalBody } from '../../../components/MySchedule/CreateShiftSwapRequestModalBody';
import {
  getMonthlyRangeEnd,
  getMonthlyRangeStart,
  getMonthlyStatsRangeEnd,
} from '../../../components/MySchedule/MonthlySchedule/utils';
import { MyScheduleMonthly } from '../../../components/MySchedule/MyScheduleMonthly';
import { MyScheduleTypeSwitcher } from '../../../components/MySchedule/MyScheduleTypeSwitcher';
import {
  RECIPIENT_SHIFT_HAS_ALREADY_STARTED_KEY,
  REQUESTER_SHIFT_HAS_ALREADY_STARTED_KEY,
} from '../../../components/MySchedule/validations';
import { useSidebar } from '../../../components/layout/Sidebar';
import {
  AgentScheduleStats,
  PtoRequest,
  PtoRequestSubtype,
  RecipientShiftForSwapInput,
  RequestPreview,
  RequestState,
  RequestSubtype,
  RequestType,
  RequesterShiftForSwapInput,
  ShiftSwapRequest,
  ShiftSwapRequestAction,
  ShiftSwapRequestController,
  useCreatePtoRequestMutation,
  useCreateShiftSwapRequestMutation,
  useGetAgentProductionOverviewScheduleQuery,
  useGetScheduleElementsQuery,
  useUpdateStateOfPtoRequestMutation,
  useUpdateStateOfShiftSwapRequestMutation,
} from '../../../graphql/components';
import { AgentsRequestsSidebar } from './AgentRequestsSidebar';
import { CalendarExport } from './CalendarExport';

import type { PTORequestFormValues, ShiftSwapRequestFormValues } from '../../../components/MySchedule/types';
import type { CalendarDate } from '@internationalized/date';
import type { FormApi, Mutator, ValidationErrors } from 'final-form';

export type CreatePTORequestModal =
  | { state: 'ready' }
  | {
      state: 'open';
      initialValues: Partial<PTORequestFormValues>;
    };

export type CreateShiftSwapRequestModal =
  | { state: 'ready' }
  | {
      state: 'open';
      initialValues: Partial<ShiftSwapRequestFormValues>;
    };

/**
 * Helper with error guard used for parse query date into valid CalendarDate
 * @param date an ISO 8601 date string, with no time components
 * @param backupDate
 */
export const parseQueryDateToCalendarDate = (date: string, backupDate: CalendarDate) => {
  try {
    return parseDate(date);
  } catch (error) {
    return backupDate;
  }
};

export const SchedulingAgentsMonthlyViewScene: React.FC = () => {
  const { t } = useTranslation();
  const { userId: agentId, locale, timezone, userHasPermissions } = useAuth();

  const history = useHistory();
  const location = useLocation();
  const { toggleSidebar, closeSidebar, isSidebarOpen } = useSidebar();

  const searchParams = new URLSearchParams(location.search);
  const queryDate = searchParams.get('date');
  const queryRequestType = searchParams.get('requestType');
  const queryRequestId = searchParams.get('requestId');

  useEffect(() => {
    if (queryRequestType && queryRequestId && !isSidebarOpen) {
      // open sidebar when not opened yet - when scene got loaded with URL query params
      toggleSidebar();
    }
  }, [isSidebarOpen, queryRequestType, queryRequestId, toggleSidebar]);

  const todayDate = today(timezone);
  const initialDate = queryDate ? parseQueryDateToCalendarDate(queryDate, todayDate) : todayDate;

  const defaultDate = startOfMonth(initialDate);

  const [selectedDate, setSelectedDate] = useState(toCalendarDate(defaultDate));
  const [createPTORequestModal, setCreatePTORequestModal] = useState<CreatePTORequestModal>({ state: 'ready' });
  const [createShiftSwapRequestModal, setCreateShiftSwapRequestModal] = useState<CreateShiftSwapRequestModal>({
    state: 'ready',
  });

  const createPTORequestModalId = useId();
  const createShiftSwapRequestModalId = useId();
  const monthFormatter = useDateFormatter({ month: 'long', timeZone: timezone });

  const months = useMemo(() => {
    const yearStart = startOfYear(selectedDate);
    return [...new Array(12).keys()].map((index) => yearStart.add({ months: index }));
  }, [selectedDate]);

  const monthsToOptions = months.map((month) => ({
    id: month.toString(),
    name: monthFormatter.format(month.toDate(timezone)),
  }));

  useEffect(() => {
    searchParams.set('date', selectedDate.toString());

    history.push({ search: searchParams.toString() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  // start date of calculated stats data
  const startOfMonthDate = startOfMonth(selectedDate);
  const startOfMonthDateZoned = startOfMonthDate.toDate(timezone);

  // end date of calculated stats data
  const endOfMonthDate = getMonthlyStatsRangeEnd(selectedDate);
  const endOfMonthDateZoned = endOfMonthDate.toDate(timezone);

  // start date of calendar range (with possibility to reach some days of the previous month)
  const startOfViewedRange = getMonthlyRangeStart(selectedDate, locale);
  const startOfViewedRangeZoned = startOfViewedRange.toDate(timezone);

  // end date of calendar range (with possibility to reach some days of the proceeding month)
  const endOfViewedRange = getMonthlyRangeEnd(selectedDate, locale);
  const endOfViewedRangeZoned = endOfViewedRange.toDate(timezone);

  const canViewRequests = userHasPermissions([Permissions.AgentViewRequests]);

  const { data: scheduleElementsData, loading: isScheduleElementsDataLoading } = useGetScheduleElementsQuery();
  const {
    data: agentScheduleOverviewData,
    loading: isAgentScheduleOverviewDataLoading,
    refetch: refetchAgentScheduleOverviewData,
  } = useGetAgentProductionOverviewScheduleQuery({
    variables: {
      agentId,
      timezone,
      startDateTime: startOfViewedRangeZoned.toISOString(),
      endDateTime: endOfViewedRangeZoned.toISOString(),
      startDateTimeStats: startOfMonthDateZoned.toISOString(),
      endDateTimeStats: endOfMonthDateZoned.toISOString(),
    },
    fetchPolicy: 'network-only',
  });

  const [createPTORequest, { loading: isCreatePTORequestMutationInProgress }] = useCreatePtoRequestMutation();
  const [updateStateOfPTORequest, { loading: isPTORequestStateUpdateInProgress }] =
    useUpdateStateOfPtoRequestMutation();
  const [updateStateOfShiftSwapRequest, { loading: isShiftSwapRequestStateUpdateInProgress }] =
    useUpdateStateOfShiftSwapRequestMutation();

  const [createShiftSwapRequest, { loading: isCreateShiftSwapRequestMutationInProgress }] =
    useCreateShiftSwapRequestMutation();

  const isLoading = isScheduleElementsDataLoading || isAgentScheduleOverviewDataLoading;
  const isMutationInProgress =
    isCreatePTORequestMutationInProgress ||
    isPTORequestStateUpdateInProgress ||
    isShiftSwapRequestStateUpdateInProgress ||
    isCreateShiftSwapRequestMutationInProgress;

  const cancelCreationOfPTORequest = () => {
    setCreatePTORequestModal({ state: 'ready' });
  };

  const cancelCreationOfShiftSwapRequest = () => {
    setCreateShiftSwapRequestModal({ state: 'ready' });
  };

  const initiateCreationOfNewRequest = (type: RequestType, subtype: RequestSubtype, date?: Date) => {
    const parsedDate = date && toCalendarDateTime(parseAbsoluteToLocal(date.toISOString()));

    if (type === RequestType.ShiftSwap) {
      setCreateShiftSwapRequestModal({
        state: 'open',
        initialValues: {
          requesterShift: {
            requesterId: agentId,
          },
          dateFromForSuitableShifts: parsedDate ?? null,
        },
      });
    } else if (type === RequestType.Pto && [RequestSubtype.Vacation, RequestSubtype.Sick].includes(subtype)) {
      setCreatePTORequestModal({
        state: 'open',
        initialValues: {
          type: subtype as unknown as PtoRequestSubtype,
          ...(parsedDate && {
            range: {
              start: parsedDate,
              end: parsedDate,
            },
          }),
          useSpecificTime: false,
        },
      });
    }
  };

  const closeRequestDetail = () => {
    searchParams.delete('requestType');
    searchParams.delete('requestId');
    searchParams.delete('requestCreated');

    history.push({ search: searchParams.toString() });
  };

  const handleCreatePTORequest = async (values: PTORequestFormValues, form: FormApi<PTORequestFormValues>) => {
    const { range, useSpecificTime, ...restValues } = values;
    const formState = form.getState();

    if (formState.hasValidationErrors) {
      /**
       * Returning submit error in order to mark invalid fields as invalid in case of not touched before attempt to submit the form.
       * The message is not meant to be presented to user.
       */
      return 'Submit error';
    }

    /* eslint-disable @typescript-eslint/no-non-null-assertion */
    const startDateTime = useSpecificTime ? range!.start : range!.start.set({ hour: 0, minute: 0 });
    const endDateTime = useSpecificTime ? range!.end : range!.end.set({ hour: 0, minute: 0 }).add({ days: 1 });
    /* eslint-enable @typescript-eslint/no-non-null-assertion */

    try {
      await createPTORequest({
        variables: {
          agentId,
          parameters: {
            ...restValues,
            dateRange: {
              startDateTime: startDateTime.toDate(timezone).toISOString(),
              endDateTime: endDateTime.toDate(timezone).toISOString(),
            },
          },
        },
      });

      closeRequestDetail(); // in case of some request detail was opened it needs to be closed
      closeSidebar(); // close sidebar with request as new request doesn't have to match the filtered criteria anyway...

      cancelCreationOfPTORequest(); // closing modal and resetting pre-opened state
    } catch (e) {
      // `customFetch()` will display error message
    }

    return null;
  };

  const handleCreateShiftSwapRequest = async (
    values: ShiftSwapRequestFormValues,
    form: FormApi<ShiftSwapRequestFormValues>
  ) => {
    // trigger re-validation of form values as we check if the shift start has not passed yet
    form.batch(() => {
      form.mutators.mutateValue('requesterShift.from');
      form.mutators.mutateValue('recipientShift.from');
    });

    const formState = form.getState();

    if (formState.hasValidationErrors) {
      if (formState.errors?.isSelectedDaysShiftSwappable === REQUESTER_SHIFT_HAS_ALREADY_STARTED_KEY) {
        notify.error(t(REQUESTER_SHIFT_HAS_ALREADY_STARTED_KEY));

        // clears recipient shift if one was selected (so that users doesn't see the error without option to unselect that shift manually as the suitable recipients shifts should be emptied)
        form.change('recipientShift', undefined);
      }

      if (formState.errors?.recipientShift === RECIPIENT_SHIFT_HAS_ALREADY_STARTED_KEY) {
        notify.error(t(RECIPIENT_SHIFT_HAS_ALREADY_STARTED_KEY));
      }

      /**
       * Returning submit error in order to mark invalid fields as invalid in case of not touched before attempt to submit the form.
       * The message is not meant to be presented to user.
       */
      return 'Submit error';
    }

    try {
      await createShiftSwapRequest({
        variables: {
          requesterId: agentId,
          parameters: {
            message: values.message,
            requesterShift: {
              ...values.requesterShift,
              from: values.requesterShift.from?.toDate(timezone).toISOString(),
            } as RequesterShiftForSwapInput,
            recipientShift: {
              ...values.recipientShift,
              from: values.recipientShift?.from?.toDate(timezone).toISOString(),
            } as RecipientShiftForSwapInput,
          },
        },
      });

      closeRequestDetail(); // in case of some request detail was opened it needs to be closed
      closeSidebar(); // close sidebar with requests as new request doesn't have to match the filtered criteria anyway...

      cancelCreationOfShiftSwapRequest(); // closing modal and resetting pre-opened state
    } catch (e) {
      // `customFetch()` will display error message
    }

    return null;
  };

  const evaluateSubmitShiftSwapErrors = (errors: ValidationErrors) => {
    if (!errors || !Object.keys(errors).length) {
      return;
    }

    if (Object.keys(errors).length === 1 && errors.recipientShift === 'validation.required') {
      notify.warning(t('schedule.request.newShiftSwapSubmittedWithoutSelectedShift'));
    }
  };

  const handleCancelRequest = async (request: RequestPreview, message: string | undefined) => {
    const { id: requestId, state, subtype, type } = request;

    if (type === RequestType.Pto) {
      try {
        const result = await updateStateOfPTORequest({
          variables: {
            agentId,
            ptoRequestId: requestId,
            parameters: {
              message,
              ptoRequestState: RequestState.Canceled,
            },
          },
        });

        return Promise.resolve(result.data!.updateStateOfPTORequest as PtoRequest);
      } catch (e) {
        // `customFetch()` will display error message
        return Promise.reject();
      }
    }

    if (type === RequestType.ShiftSwap) {
      try {
        const result = await updateStateOfShiftSwapRequest({
          variables: {
            agentId,
            requestId,
            action: ShiftSwapRequestAction.Cancel,
            controller:
              subtype === RequestSubtype.Requester
                ? ShiftSwapRequestController.Requesters
                : ShiftSwapRequestController.Recipients,
            parameters: {
              fromState: state,
              message,
            },
          },
        });

        return Promise.resolve(result.data!.updateStateOfShiftSwapRequest as ShiftSwapRequest);
      } catch (e) {
        // `customFetch()` will display error message
        return Promise.reject();
      }
    }

    return Promise.reject();
  };

  const scheduleElements = scheduleElementsData?.scheduleElements ?? [];

  /**
   * mutator which should re-trigger validation of form value when needed
   * https://github.com/final-form/react-final-form/issues/559#issuecomment-510792155
   */
  const mutateValue: Mutator<ShiftSwapRequestFormValues> = ([name], state, { changeValue }) => {
    // change the value to the same value, thus
    // triggering a revalidation of the same value
    changeValue(state, name, (value) => value);
  };

  return (
    <>
      <Header title={t(`schedule.mySchedule.mySchedule`)} testId="my-schedule:monthly-view:title" />

      <Panel>
        <Stack direction="horizontal" spacing={15} className="w-full">
          <MyScheduleTypeSwitcher type="weekly" t={t} />

          <Button
            aria-label={t('date.previousMonth')}
            onClick={() => setSelectedDate(selectedDate.subtract({ months: 1 }))}
            testId="button-previous-month"
            variant="quiet"
          >
            <ArrowLeftIcon />
          </Button>

          <Select
            className="month-select"
            label={t('date.month')}
            options={monthsToOptions}
            onSelectionChange={(values) => {
              setSelectedDate(parseDate(values.values().next().value));
            }}
            selectionMode="single"
            selectedKeys={[selectedDate.toString()]}
            testId="input-monthpicker"
          />

          <Button
            aria-label={t('date.nextMonth')}
            onClick={() => setSelectedDate(selectedDate.add({ months: 1 }))}
            testId="button-next-month"
            variant="quiet"
          >
            <ArrowRightIcon />
          </Button>

          <NumberField
            aria-label={t('date.year')}
            className="year-select"
            formatOptions={{ useGrouping: false }}
            onChange={(value) => setSelectedDate(selectedDate.set({ year: value }))}
            value={selectedDate.year}
          />

          {!isSameMonth(selectedDate, todayDate) && (
            <Tooltip content={t('date.backToCurrentDay')}>
              <Button
                aria-label={t('date.backToCurrentDay')}
                onClick={() => setSelectedDate(startOfMonth(todayDate))}
                testId="current-day-button"
                variant="outline"
              >
                <CalendarCurrentDayIcon />
              </Button>
            </Tooltip>
          )}
          <ButtonGroup className="mis-auto" gap={25}>
            {canViewRequests && (
              <Button
                isSelected={isSidebarOpen}
                onClick={toggleSidebar}
                variant="outline"
                testId="my-schedule:monthly-view:toggle-requests-sidebar"
              >
                <QuestionSquareIcon /> {t('requests.request', { count: 2 })}
              </Button>
            )}
            <CalendarExport userId={agentId} />
          </ButtonGroup>
        </Stack>
      </Panel>

      <Content testId="my-schedule:monthly-view:container">
        {(isLoading || isMutationInProgress) && <Progress withOverlay />}

        {isLoading ? null : (
          <MyScheduleMonthly
            initiateCreationOfNewRequest={initiateCreationOfNewRequest}
            scheduleItems={agentScheduleOverviewData?.agentProductionOverviewSchedule?.scheduleDailyOverview ?? []}
            selectedDate={selectedDate.toDate(timezone)}
            scheduleElements={scheduleElements}
            overviewStats={
              agentScheduleOverviewData?.agentProductionOverviewSchedule?.stats ?? ({} as AgentScheduleStats)
            }
          />
        )}

        <Modal
          className="time-off-request"
          onClose={cancelCreationOfPTORequest}
          title={t('schedule.request.newRequest')}
          isOpen={createPTORequestModal.state === 'open'}
        >
          <Form<PTORequestFormValues>
            onSubmit={handleCreatePTORequest}
            initialValues={createPTORequestModal.state === 'open' ? createPTORequestModal.initialValues : {}}
          >
            {({ handleSubmit }) => (
              <>
                <ModalBody>
                  {/* Form is inside ModalBody so it does not break Scrollbar. */}
                  <form id={createPTORequestModalId} onSubmit={handleSubmit}>
                    <CreatePTORequestModalBody />
                  </form>
                </ModalBody>

                <ModalFooter>
                  <Button onClick={cancelCreationOfPTORequest} variant="outline" testId="modal:button-cancel">
                    {t('layout.action.cancel')}
                  </Button>

                  <Button color="info" form={createPTORequestModalId} type="submit" testId="modal:button-accept">
                    <EnvelopeFullIcon />
                    {t('schedule.request.sendRequest')}
                  </Button>
                </ModalFooter>
              </>
            )}
          </Form>
        </Modal>

        <Modal
          className="shift-swap-request"
          onClose={cancelCreationOfShiftSwapRequest}
          title={t('schedule.request.shiftSwapRequest')}
          isOpen={createShiftSwapRequestModal.state === 'open'}
        >
          <Form<ShiftSwapRequestFormValues>
            onSubmit={handleCreateShiftSwapRequest}
            initialValues={
              createShiftSwapRequestModal.state === 'open' ? createShiftSwapRequestModal.initialValues : {}
            }
            mutators={{ mutateValue }}
          >
            {({ handleSubmit, errors }) => (
              <>
                <ModalBody>
                  {/* Form is inside ModalBody so it does not break Scrollbar. */}
                  <form
                    id={createShiftSwapRequestModalId}
                    onSubmit={handleSubmit}
                    className="shift-swap-request-modal__wrapper flex flex-col h-full gap-20"
                  >
                    <CreateShiftSwapRequestModalBody scheduleElements={scheduleElements} />
                  </form>
                </ModalBody>

                <ModalFooter>
                  <Button onClick={cancelCreationOfShiftSwapRequest} variant="outline" testId="modal:button-cancel">
                    {t('layout.action.cancel')}
                  </Button>

                  <Button
                    color="info"
                    form={createShiftSwapRequestModalId}
                    type="submit"
                    testId="modal:button-accept"
                    onClick={() => evaluateSubmitShiftSwapErrors(errors)}
                  >
                    <EnvelopeFullIcon />
                    {t('schedule.request.sendRequest')}
                  </Button>
                </ModalFooter>
              </>
            )}
          </Form>
        </Modal>
      </Content>

      <SidebarWrapper>
        {isSidebarOpen && canViewRequests && (
          <AgentsRequestsSidebar
            onClose={closeSidebar}
            onCreateRequest={initiateCreationOfNewRequest}
            onCancelRequest={handleCancelRequest}
            refetchData={refetchAgentScheduleOverviewData}
            scheduleElements={scheduleElements}
          />
        )}
      </SidebarWrapper>
    </>
  );
};
